import { CoreFetch, DocumentApi, EmailApi, FaxApi, LetterApi, MailApi, PhoneApi } from '../../api';
import { fileCabinetCall, fileCabinetPage } from './file-cabinet.api';
import type { FileCabinetClients } from './file-cabinet.api-source';

/**
 * The live provider-app clients: the generated client behind `CoreFetch`, so requests carry the
 * session and pass the demo-mode guard like every other call (NS-14). Nothing generated is edited
 * (NS-16). Queries arrive as the endpoint's own parameters (`file-cabinet.api-source.ts`).
 *
 * `beforeRequest` runs right before each call. The provider page uses it to put the requests in a
 * fetch group that skips the app's global error dialog: this screen says what failed itself, and a
 * dialog per failed count — one account answers 403 on every mail and fax list — would bury it.
 */
export function createFileCabinetProviderClients(beforeRequest: () => void = (): void => undefined): FileCabinetClients {
    const call = <T>(run: () => Promise<T>): Promise<T> => {
        beforeRequest();
        return fileCabinetCall(run);
    };
    const documents = (): DocumentApi => CoreFetch<DocumentApi>(DocumentApi);
    const mail = (): MailApi => CoreFetch<MailApi>(MailApi);
    const letters = (): LetterApi => CoreFetch<LetterApi>(LetterApi);
    const faxes = (): FaxApi => CoreFetch<FaxApi>(FaxApi);
    const emails = (): EmailApi => CoreFetch<EmailApi>(EmailApi);
    const phones = (): PhoneApi => CoreFetch<PhoneApi>(PhoneApi);

    return {
        listDocuments: q => call(async () => fileCabinetPage(await documents().listProviderDocumentsRaw(q))),
        listMail: q => call(async () => fileCabinetPage(await mail().listProviderMailRaw(q))),
        listLetters: q => call(async () => fileCabinetPage(await letters().listProviderLettersRaw(q))),
        listReceivedFaxes: q => call(async () => fileCabinetPage(await faxes().listProviderReceiveFaxesRaw(q))),
        listSentFaxes: q => call(async () => fileCabinetPage(await faxes().listProviderSendFaxesRaw(q))),
        listSentEmails: q => call(async () => fileCabinetPage(await emails().listProviderSendEmailsRaw(q))),
        listCalls: q => call(async () => fileCabinetPage(await phones().listProviderPhoneRaw(q))),
        listAiCalls: q => call(async () => fileCabinetPage(await phones().getProviderAICallsRaw(q))),
        getDocument: id => call(() => documents().getProviderDocument({ id })),
        getMail: id => call(() => mail().getProviderMail({ id })),
        getLetter: id => call(() => letters().getProviderLetter({ id })),
        getReceivedFax: id => call(() => faxes().getProviderReceiveFax({ id })),
        getSentFax: id => call(() => faxes().getProviderSendFax({ id })),
        getSentEmail: id => call(() => emails().getProviderSendEmail({ id })),
        getCall: id => call(() => phones().getProviderPhone({ id })),
        getAiCall: id => call(() => phones().getProviderAICall({ id })),
        transcription: id => call(async () => documents().getProviderDocumentTranscription({ id })),
        documentUrl: id => call(async () => (await documents().getProviderDocumentUrl({ id })).url),
        documentEmbedUrl: id => call(async () => (await documents().getProviderEmbedDocumentUrl({ id })).url),
        letterUrl: id => call(async () => (await letters().viewProviderLetter({ id })).url),
        sentFaxUrl: id => call(async () => (await faxes().viewProviderSendFax({ id })).url),
    };
}
